// src/components/Chat/useMenuData.js
// Hook que lee en tiempo real las colecciones de menú desde Firestore
// NOTA: Este hook abría 19 listeners de Firestore DUPLICADOS con App.js,
// causando ~$948/mes en lecturas excesivas. Ahora usa cache de localStorage
// que App.js ya mantiene actualizado, sin abrir listeners propios.
import { useState, useEffect } from 'react';

const useMenuData = () => {
  // Almuerzo
  const [soups, setSoups] = useState([]);
  const [soupReplacements, setSoupReplacements] = useState([]);
  const [proteins, setProteins] = useState([]);
  const [principles, setPrinciples] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [sides, setSides] = useState([]);
  const [additions, setAdditions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Desayuno
  const [breakfastTypes, setBreakfastTypes] = useState([]);
  const [breakfastEggs, setBreakfastEggs] = useState([]);
  const [breakfastBroths, setBreakfastBroths] = useState([]);
  const [breakfastDrinks, setBreakfastDrinks] = useState([]);
  const [breakfastProteins, setBreakfastProteins] = useState([]);
  const [breakfastAdditions, setBreakfastAdditions] = useState([]);
  const [breakfastRiceBread, setBreakfastRiceBread] = useState([]);
  const [breakfastTimes, setBreakfastTimes] = useState([]);
  const [times, setTimes] = useState([]);

  // Horarios y configuración
  const [schedules, setSchedules] = useState({
    breakfastStart: 420,
    breakfastEnd: 631,
    lunchStart: 632,
    lunchEnd: 950,
  });
  const [isOrderingDisabled, setIsOrderingDisabled] = useState(false);

  const [loaded, setLoaded] = useState(false);

  // Leer datos desde el cache de localStorage que App.js mantiene actualizado.
  // NO abrimos listeners propios de Firestore para evitar duplicar lecturas.
  useEffect(() => {
    const collectionMap = {
      soups: setSoups,
      soupReplacements: setSoupReplacements,
      proteins: setProteins,
      principles: setPrinciples,
      drinks: setDrinks,
      sides: setSides,
      additions: setAdditions,
      paymentMethods: setPaymentMethods,
      breakfastTypes: setBreakfastTypes,
      breakfastEggs: setBreakfastEggs,
      breakfastBroths: setBreakfastBroths,
      breakfastDrinks: setBreakfastDrinks,
      breakfastProteins: setBreakfastProteins,
      breakfastAdditions: setBreakfastAdditions,
      breakfastRiceBread: setBreakfastRiceBread,
      breakfastTimes: setBreakfastTimes,
      times: setTimes,
    };

    const loadFromCache = () => {
      try {
        Object.entries(collectionMap).forEach(([col, setter]) => {
          const cached = localStorage.getItem('cached_' + col);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setter(parsed);
            }
          }
        });
      } catch (_) {}
      setLoaded(true);
    };

    // Cargar inmediatamente
    loadFromCache();

    // Escuchar cuando App.js actualiza las colecciones
    const handleUpdate = () => loadFromCache();
    window.addEventListener('optionsUpdated', handleUpdate);

    return () => {
      window.removeEventListener('optionsUpdated', handleUpdate);
    };
  }, []);

  // Helpers
  const available = (items) => items.filter(i => !i.isFinished);
  const formatPrice = (p) => p ? `$${Number(p).toLocaleString('es-CO')}` : '';

  const getCurrentMenuType = () => {
    if (isOrderingDisabled) return 'closed';
    const now = new Date();
    const totalMinutes = now.getHours() * 60 + now.getMinutes();
    if (totalMinutes >= schedules.breakfastStart && totalMinutes <= schedules.breakfastEnd) return 'breakfast';
    if (totalMinutes >= schedules.lunchStart && totalMinutes <= schedules.lunchEnd) return 'lunch';
    return 'closed';
  };

  return {
    // Raw data
    soups, soupReplacements, proteins, principles, drinks, sides, additions, paymentMethods,
    breakfastTypes, breakfastEggs, breakfastBroths, breakfastDrinks,
    breakfastProteins, breakfastAdditions, breakfastRiceBread, breakfastTimes,
    times,
    schedules, isOrderingDisabled,
    loaded,
    // Helpers
    available,
    formatPrice,
    getCurrentMenuType,
  };
};

export default useMenuData;
